import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {datacenterActions, providersActions, marketActions, geoActions} from "../actions"
import {catchError, exhaustMap, map, mergeMap, switchMap, of, withLatestFrom} from "rxjs";
import {DataCentersService} from "../../service/data-centers.service";
import {DatacenterV2} from "../../model/datacenter-v2";
import {Provider} from "../../model/providers";

@Injectable()
export class DatacenterEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store,
    private datacenterService: DataCentersService
  ) {
  }

  loadPaginatedDatacenters = createEffect(() =>
    this.actions$.pipe(
      ofType(datacenterActions.getDatacenters),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datacenterService.getPaginatedDatacenters(action.mapId, action.pageSize, action.lastId).pipe(
          mergeMap(response => {
            const groupedByProvider = ((response as any).data.records);
            const providers = groupedByProvider.map((providerGroup: any) => {
              return {
                id: providerGroup.providerId,
                name: providerGroup.provider,
                base64Icon: providerGroup.base64Icon,
                color: providerGroup.color,
                isVisible: true,
                hasChanged: false
              } as Provider;
            });
            const datacenters: DatacenterV2[] = groupedByProvider.flatMap((providerGroup: any) => {
              providerGroup.datacenters.forEach((datacenter: any) => {
                datacenter.providerId = providerGroup.providerId;
                datacenter.providerColor = providerGroup.color;
              });
              return providerGroup.datacenters;
            });
            const markets = new Set<string>();
            datacenters.flatMap(dc => dc.market).forEach(m => markets.add(m));

            const pageSize = action.pageSize;
            if (datacenters.length == pageSize) {
              const createResult = datacenterActions.getDatacenters({
                mapId: action.mapId,
                pageSize: pageSize,
                lastId: (response as any).data.lastId
              });
              return [datacenterActions.getDatacentersSuccess({datacenters: datacenters, lastBatch: false}),
              providersActions.providersToDisplayLoaded({providers: providers}), marketActions.addMarkets({markets: markets}),
                createResult];
            } else {
              return [providersActions.providersToDisplayLoaded({providers: providers}), marketActions.addMarkets({markets: markets}),
              datacenterActions.getDatacentersSuccess({datacenters: datacenters, lastBatch: true})];
            }
          }),
          catchError(error => of(
            datacenterActions.getDatacentersFailure({msg: error.message})
          ))
        )
      )
    ));

  loadDatacentersWithErrors = createEffect(() =>
    this.actions$.pipe(
      ofType(datacenterActions.getDatacentersWithErrors),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datacenterService.getDatacentersWithErrors(action.mapId).pipe(
          mergeMap(response => {
            const groupedByProvider = ((response as any).data.records);
            const datacenters: DatacenterV2[] = groupedByProvider.flatMap((providerGroup: any) => {
              providerGroup.datacenters.forEach((datacenter: any) => {
                datacenter.providerId = providerGroup.providerId;
                datacenter.providerColor = providerGroup.color;
              });
              return providerGroup.datacenters;
            });

            return [datacenterActions.getDatacentersWithErrorsSuccess({datacenters: datacenters})];

          }),
          catchError(error => of(
            datacenterActions.getDatacentersWithErrorsFailure({msg: error.message})
          ))
        )
      )
    ));

  getDatacentersByProvider = createEffect(() =>
    this.actions$.pipe(
      ofType(datacenterActions.getDatacentersByProvider),
      withLatestFrom(this.store),
      exhaustMap(([action, store]) => this.datacenterService.getDatacentersByProvider(action.mapId, action.provider.name).pipe(
        map(response => {
          return datacenterActions.getDatacentersByProviderSuccess({
            provider: action.provider,
            datacenters: (response as any).data as DatacenterV2[]
          })
        }),
        catchError((error: any) => {
          return of(datacenterActions.getDatacentersFailure({msg: "error"}));
        })
      ))
    ));
}
